// Reglas puras de la vista previa de «Actualizar desde Zentral»: conteos, grupos y orden.
import type { ReconciliationPreviewItem } from '@fiado/type-kit/bin/retailIntegration/dtos/ReconciliationPreview';
import type { ReconciliationRunCounts } from '@fiado/type-kit/bin/retailIntegration/dtos/ReconciliationRunCounts';
import { RetailIntegrationModeEnum } from '@fiado/type-kit/bin/retailOrg/enums/RetailIntegrationModeEnum';

export type PreviewGroup = 'created' | 'updated' | 'deactivated' | 'reactivated';

/** Orden en que se pintan las tarjetas y las listas. */
export const PREVIEW_GROUPS: readonly PreviewGroup[] = [
    'created',
    'updated',
    'deactivated',
    'reactivated',
];

/** Campos de `fields` que se muestran con la misma etiqueta; el resto usa la suya en i18n. */
const FIELD_ALIASES: Readonly<Record<string, string>> = {
    currentPriceSk: 'basePriceCents',
    inactiveAt: 'inactiveReason',
    inactiveRunId: 'inactiveReason',
    displayName: 'name',
};

/** Campos internos que no le dicen nada a quien revisa la vista previa. */
const HIDDEN_FIELDS: ReadonlySet<string> = new Set(['source']);

/** En SOFT la revisión no da bajas; sin modo se trata como FULL, igual que retail-org. */
export function appliesDeactivations(mode: RetailIntegrationModeEnum | null): boolean {
    return mode !== RetailIntegrationModeEnum.SOFT;
}

/** Cambios que de verdad escribiría «Aplicar»; las bajas no cuentan en SOFT. */
export function changesToApply(
    counts: ReconciliationRunCounts,
    mode: RetailIntegrationModeEnum | null,
): number {
    const deactivated = appliesDeactivations(mode) ? counts.deactivated : 0;
    return counts.created + counts.updated + counts.reactivated + deactivated;
}

/** Renglones de un grupo que la lista no trae porque se cortó en el tope. */
export function hiddenRows(listed: number, counted: number): number {
    return Math.max(0, counted - listed);
}

/** Campos de un renglón, ya normalizados y sin repetir, en el orden en que llegaron. */
export function previewFieldKeys(fields: readonly string[] | undefined): string[] {
    const keys: string[] = [];
    for (const field of fields ?? []) {
        if (HIDDEN_FIELDS.has(field)) continue;
        const key = FIELD_ALIASES[field] ?? field;
        if (!keys.includes(key)) keys.push(key);
    }
    return keys;
}

/** Nombre a mostrar del renglón: su etiqueta o, sin ella, su id. */
export function previewItemName(item: ReconciliationPreviewItem): string {
    return item.label?.trim() ? item.label : item.id;
}

/** Renglones en orden alfabético por nombre; el id desempata. */
export function sortPreviewItems(
    items: readonly ReconciliationPreviewItem[],
): ReconciliationPreviewItem[] {
    return [...items].sort(
        (a, b) =>
            previewItemName(a).localeCompare(previewItemName(b), 'es-MX') ||
            a.id.localeCompare(b.id, 'es-MX'),
    );
}
